import LinkButton from '@carvana/ds-link-button';
import React from 'react';
import { StepName } from '|/app/services';
import CardBrandIcon, { isKnownCardBrand } from '|/components/carvana-ds/card-brand-icon';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './payment-details.module.css';
import useOrderSummaryVm from '../../use-order-summary-vm';

interface PaymentDetailsProps {
  summary?: ReviewSummaryResponse;
  testId?: string;
}

const getCardDisplay = ({
  mask,
  cardBrand
}: {
  mask?: string | null;
  cardBrand?: string | null;
}) => {
  if (!mask) {
    return 'Card ****';
  }

  return (
    <span className={styles.cardLine}>
      <CardBrandIcon brand={cardBrand ?? undefined} />
      {!isKnownCardBrand(cardBrand ?? undefined) && `${cardBrand ? 'Card' : 'Account'} `}
      **** {mask}
    </span>
  );
};

const getDigitalWalletDisplayName = (digitalWallet?: string | null) => {
  switch (digitalWallet) {
    case 'ApplePay':
      return 'Apple Pay';
    case 'GooglePay':
      return 'Google Pay';
    default:
      return 'Apple Pay or Google Pay';
  }
};

const getPaymentSelectionDisplay = ({
  cardBrand,
  digitalWallet,
  mask,
  selectionType
}: {
  cardBrand?: string | null;
  digitalWallet?: string | null;
  mask?: string | null;
  selectionType?: string | null;
}) => {
  switch (selectionType) {
    case 'BankAccount':
      return mask ? `Account **** ${mask}` : 'Account ****';
    case 'Card':
      return getCardDisplay({ mask, cardBrand });
    case 'DigitalWallet':
      return getDigitalWalletDisplayName(digitalWallet);
    case 'CashiersCheck':
      return "Cashier's Check";
    case 'WireTransfer':
      return 'Wire Transfer';
    default:
      return null;
  }
};

const PaymentDetails = ({ summary, testId = 'payment-details-section' }: PaymentDetailsProps) => {
  const navigateToStep = useNavigateToStep();
  const {
    isCarvanaFinancing,
    isCash,
    isThirdParty,
    downPaymentAccount,
    enrolledInAutopay,
    autopayAccount,
    dueAtOrder,
    refundDue
  } = useOrderSummaryVm(summary ?? ({} as ReviewSummaryResponse));

  const isSafWithDue = isThirdParty && (dueAtOrder ?? 0) > 0;
  const hasPaymentDetails = isCarvanaFinancing || isCash || isSafWithDue;
  const accountWillBeUsed = (dueAtOrder ?? 0) > 0 || (refundDue ?? 0) > 0;
  const paymentMethodDisplay = downPaymentAccount
    ? getPaymentSelectionDisplay(downPaymentAccount)
    : null;
  const hasPaymentMethodDetails = !!paymentMethodDisplay;
  const shouldShowPaymentMethodDetails =
    hasPaymentMethodDetails &&
    (isCash || isSafWithDue || (isCarvanaFinancing && accountWillBeUsed));
  const shouldShowEditPaymentMethod =
    downPaymentAccount?.selectionType !== 'CashiersCheck' &&
    downPaymentAccount?.selectionType !== 'WireTransfer';
  const shouldShowAutopayDetails = isCarvanaFinancing && enrolledInAutopay !== null;

  if (!hasPaymentDetails) return null;

  return (
    <section data-testid={testId}>
      <h3>Payment details</h3>
      <div className={styles.paymentDetailsContainer}>
        {shouldShowAutopayDetails && (
          <div className={styles.paymentMethod}>
            <div className={styles.paymentDetails}>
              <h4>Payment method for auto pay</h4>
              {!enrolledInAutopay && <p>Auto pay declined</p>}
              {autopayAccount && enrolledInAutopay && (
                <p>{`Account **** ${autopayAccount.mask}`}</p>
              )}
            </div>
            <LinkButton
              onClick={() => {
                navigateToStep(StepName.Autopay);
              }}
              data-analytics-id="pp_review_order_edit_auto_pay"
              size="sm"
              underline={false}
              className={styles.editButton}
            >
              Edit auto pay
            </LinkButton>
          </div>
        )}

        {shouldShowPaymentMethodDetails ? (
          <div className={styles.paymentMethod}>
            <div className={styles.paymentDetails}>
              <h4>Payment method for {refundDue ? 'refund due' : 'total due at order'}</h4>
              <p>{paymentMethodDisplay}</p>
            </div>
            {shouldShowEditPaymentMethod && (
              <LinkButton
                onClick={() => {
                  navigateToStep(StepName.Payment);
                }}
                data-analytics-id="pp_review_order_edit_payment"
                size="sm"
                underline={false}
                className={styles.editButton}
              >
                Edit payment method
              </LinkButton>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PaymentDetails;
